import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CARBONATES, CARBONATES_BY_FORMULA, UnitLabel } from '@/shared/config/config'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DataBlock,
  NumericInputGroup,
  SelectGroup
} from '@/common'
import Drawer from '@/shared/components/ui/Drawer'
import { usePermissions } from '@/shared/hooks/usePermissions'
import calculateProcessEmissions from '@/features/reports/formulas/calculateProcessEmissions'

export type CementClinkerRawMealComposition = {
  emission: number
  activityData: string
  emissionFactor: string
  conversionFactor: string
  carbonate: string
}

const getInitialState = () => {
  return {
    emission: 0,
    activityData: '',
    emissionFactor: '',
    conversionFactor: '1',
    carbonate: ''
  }
}

export default function CementClinkerRawMealCompositionCalculatorPanel({
  isOpen,
  close,
  onSubmit
}: {
  isOpen: boolean
  close: () => void
  onSubmit: (rawMealComposition: CementClinkerRawMealComposition) => void
}) {
  const { t } = useTranslation()
  const { checkPermission } = usePermissions()
  const canEditReport = checkPermission('emissionReport', 'update')

  const [state, setState] = useState<CementClinkerRawMealComposition>(getInitialState())

  const handleChange = useCallback(
    (field: keyof CementClinkerRawMealComposition, value: string) => {
      const updated = { ...state, [field]: value }
      if (field === 'carbonate') {
        const carbonate = CARBONATES_BY_FORMULA[value]
        if (carbonate) {
          updated.emissionFactor = carbonate.emissionFactor
        }
      }

      setState(updated)
    },
    [state]
  )

  useEffect(() => {
    const emission = calculateProcessEmissions({
      activityData: Number(state.activityData),
      emissionFactor: Number(state.emissionFactor),
      conversionFactor: Number(state.conversionFactor)
    })
    setState({ ...state, emission })
  }, [state.activityData, state.emissionFactor, state.conversionFactor])

  const onAddClick = useCallback(() => {
    onSubmit(state)
    setState(getInitialState())
    close()
  }, [close, onSubmit, state])

  const isValid = state.emission > 0 && state.carbonate

  return (
    <Drawer isOpen={isOpen} close={close}>
      <Card>
        <CardHeader>
          <CardTitle title={t('report:cementClinkerRawMealCompositionCalculatorPanel.title')} />
        </CardHeader>
        <CardBody className="space-y-4">
          <SelectGroup
            name="carbonate"
            label={t(`report:common:fields.carbonate.label`)}
            options={CARBONATES.map(c => ({
              label: `${c.name} (${c.formula})`,
              value: c.formula
            }))}
            value={state.carbonate}
            onChange={evt => {
              handleChange('carbonate', evt.target.value)
            }}
          />
          <NumericInputGroup
            name="activityData"
            label={t(`report:common:fields.activityData.label`)}
            value={state.activityData}
            numericFormat={{
              decimalScale: 3,
              allowNegative: false,
              thousandSeparator: ',',
              // Using the special `onValueChange` prop from the react-number-format library
              // to have access to the value without the thousand separators
              onValueChange: values => {
                handleChange('activityData', values.value)
              }
            }}
            suffix={UnitLabel.t}
          />
          <NumericInputGroup
            name="emissionFactor"
            label={t(`report:common:fields.emissionFactor.label`)}
            value={state.emissionFactor}
            onChange={evt => {
              handleChange('emissionFactor', evt.target.value)
            }}
            suffix={UnitLabel.tCO2PerT}
            numericFormat={{
              decimalScale: 3
            }}
          />

          <NumericInputGroup
            name="conversionFactor"
            label={t(`report:common:fields.conversionFactor.label`)}
            value={state.conversionFactor}
            onChange={evt => {
              handleChange('conversionFactor', evt.target.value)
            }}
            numericFormat={{
              decimalScale: 3,
              isAllowed: values => {
                const { floatValue } = values
                const maxValue = 1
                return floatValue === undefined || floatValue <= maxValue
              }
            }}
          />

          <DataBlock data={`${state.emission} ${UnitLabel.tCO2}`} label={t('report:common:emission')} />
        </CardBody>
        <footer className="grid grid-cols-2 gap-4 pb-4 px-4">
          <Button onClick={close} variant="subtle">
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={!canEditReport || !isValid} onClick={onAddClick}>
            {t('common:actions.add')}
          </Button>
        </footer>
      </Card>
    </Drawer>
  )
}
